import { Download, ShieldOff, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNotify, useRedirect } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { downloadBlob } from "../reports/reportExport";

type Action = "anonymize" | "erase";

/**
 * Data-subject requests on a contact: export, anonymise, erase.
 *
 * ANONYMISE IS THE DEFAULT, ERASE THE EXCEPTION. A service business almost
 * always has invoices attached to a real customer, and those have their own
 * retention obligations, deleting them to honour an erasure request would
 * breach a different law. So the server refuses to erase a contact with
 * financial history and says to anonymise instead; this dialog explains that
 * up front rather than letting someone discover it as an error.
 */
export const PrivacyActions = ({
  contactId,
  contactName,
}: {
  contactId: number;
  contactName: string;
}) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [confirming, setConfirming] = useState<Action | null>(null);
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    setBusy(true);
    const { data, error } = await getSupabaseClient().rpc(
      "export_contact_data",
      { p_contact_id: contactId },
    );
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    // JSON rather than CSV: a subject access response is a nested document,
    // and flattening it would lose the relationships it is meant to disclose.
    downloadBlob(
      [JSON.stringify(data, null, 2)],
      `gravun-contact-${contactId}-data.json`,
      "application/json",
    );
    notify("Exported this contact's data");
  };

  const run = async (action: Action) => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc(
      action === "anonymize" ? "anonymize_contact" : "erase_contact",
      { p_contact_id: contactId },
    );
    setBusy(false);
    if (error) {
      // The erase-with-invoices refusal is a full sentence telling the user
      // what to do instead, so it is shown as-is.
      notify(error.message, { type: "warning", autoHideDuration: 10000 });
      return;
    }
    setConfirming(null);
    if (action === "erase") {
      notify("Contact erased");
      redirect("/contacts");
      return;
    }
    notify("Contact anonymized");
    window.location.reload();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5"
            disabled={busy}
          >
            <ShieldOff className="size-3.5" />
            Privacy
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem className="cursor-pointer" onClick={exportData}>
            <Download className="mr-2 size-3.5" />
            Export their data
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => setConfirming("anonymize")}
          >
            <ShieldOff className="mr-2 size-3.5" />
            Anonymize
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer text-destructive"
            onClick={() => setConfirming("erase")}
          >
            <Trash2 className="mr-2 size-3.5" />
            Erase completely
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirming === "erase" ? "Erase" : "Anonymize"} {contactName}?
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {confirming === "anonymize" ? (
              <>
                <p className="text-[13px] text-muted-foreground">
                  Removes their name, contact details, address, notes, message
                  bodies, call recordings and form answers. This cannot be
                  undone.
                </p>
                <p className="text-[13px] text-muted-foreground">
                  Jobs, estimates, invoices and payments are{" "}
                  <strong>kept</strong>, you are required to retain those, but
                  they will no longer identify anyone. The record is also marked
                  do-not-contact so nothing can reach them again.
                </p>
              </>
            ) : (
              <>
                <p className="text-[13px] text-muted-foreground">
                  Permanently deletes this contact and everything attached to
                  them. This cannot be undone.
                </p>
                <p className="text-[13px] text-muted-foreground">
                  If they have any invoices or payments this will be refused,
                  use Anonymize instead, which removes their personal data while
                  keeping the financial records you must retain.
                </p>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirming(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => run(confirming!)}
              >
                {busy
                  ? "Working…"
                  : confirming === "erase"
                    ? "Erase permanently"
                    : "Anonymize"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
